import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function formatLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function formatDateTime(value, locale = "en") {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function renderPrimitive(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return String(value);
}

function StructuredValue({ value }) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <div className="ai-message__empty-value">—</div>;
    }

    if (value.every((item) => !isPlainObject(item))) {
      return (
        <div className="ai-message__chips">
          {value.map((item, index) => (
            <span key={`${renderPrimitive(item)}-${index}`} className="ai-message__chip">
              {renderPrimitive(item)}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="ai-message__object-list">
        {value.map((item, index) => (
          <div key={index} className="ai-message__object-card">
            <StructuredValue value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return <div className="ai-message__empty-value">—</div>;
    }

    return (
      <div className="ai-message__kv-list">
        {entries.map(([key, nestedValue]) => (
          <div key={key} className="ai-message__kv-item">
            <div className="ai-message__kv-label">{formatLabel(key)}</div>
            <div className="ai-message__kv-value">
              <StructuredValue value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <span>{renderPrimitive(value)}</span>;
}

function AssistantSections({ message, isAr }) {
  const payload = isPlainObject(message?.payload) ? message.payload : {};
  const actions = Array.isArray(payload.actions) ? payload.actions : [];
  const suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];
  const data = isPlainObject(payload.data) ? payload.data : {};
  const toolCalls = Array.isArray(message?.tool_calls) ? message.tool_calls : [];
  const dataEntries = Object.entries(data).filter(([, value]) => {
    if (Array.isArray(value)) return value.length > 0;
    if (isPlainObject(value)) return Object.keys(value).length > 0;
    return value !== null && value !== undefined && value !== "";
  });

  if (
    actions.length === 0 &&
    suggestions.length === 0 &&
    dataEntries.length === 0 &&
    toolCalls.length === 0
  ) {
    return null;
  }

  return (
    <div className="ai-message__sections">
      {actions.length > 0 ? (
        <div className="ai-message__section">
          <div className="ai-message__section-title">
            {isAr ? "إجراءات مقترحة" : "Suggested actions"}
          </div>
          <div className="ai-message__actions">
            {actions.map((action, index) => {
              const label = action?.label ? formatLabel(action.label) : (isAr ? "فتح" : "Open");
              const route = action?.route ? String(action.route) : "";

              if (route) {
                return (
                  <Link
                    key={`${route}-${index}`}
                    to={route}
                    className="ai-message__action-link"
                  >
                    {label}
                  </Link>
                );
              }

              return (
                <span key={`${label}-${index}`} className="ai-message__chip">
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      ) : null}

      {suggestions.length > 0 ? (
        <div className="ai-message__section">
          <div className="ai-message__section-title">
            {isAr ? "اقتراحات متابعة" : "Suggested follow-up"}
          </div>
          <div className="ai-message__chips">
            {suggestions.map((suggestion, index) => (
              <span key={`${suggestion}-${index}`} className="ai-message__chip">
                {suggestion}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {dataEntries.length > 0 ? (
        <div className="ai-message__section">
          <div className="ai-message__section-title">
            {isAr ? "بيانات مرتبطة" : "Related data"}
          </div>
          <div className="ai-message__data-grid">
            {dataEntries.map(([key, value]) => (
              <div key={key} className="ai-message__data-card">
                <div className="ai-message__data-title">{formatLabel(key)}</div>
                <StructuredValue value={value} />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {toolCalls.length > 0 ? (
        <details className="ai-message__tools">
          <summary>{isAr ? "نشاط الأدوات" : "Tool activity"}</summary>
          <div className="ai-message__tool-list">
            {toolCalls.map((toolCall, index) => (
              <div key={`${toolCall?.id || toolCall?.tool_name || "tool"}-${index}`} className="ai-message__tool-item">
                <span className="ai-message__tool-name">{toolCall?.tool_name || "Tool"}</span>
                <span className={`ai-message__tool-status ai-message__tool-status--${toolCall?.status || "unknown"}`}>
                  {toolCall?.status || "unknown"}
                </span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function MessageBubble({ message, isAr }) {
  const locale = isAr ? "ar" : "en";
  const isAssistant = message?.role === "assistant";

  return (
    <article
      className={`ai-message ai-message--${message?.role || "assistant"} ${
        isAssistant ? "ai-message--assistant" : "ai-message--user"
      }`}
    >
      <div className="ai-message__meta">
        <span className="ai-message__role">
          {isAssistant ? (isAr ? "المساعد" : "Assistant") : (isAr ? "أنت" : "You")}
        </span>
        {message?.agent ? (
          <span className="ai-message__agent">{formatLabel(message.agent)}</span>
        ) : null}
        {message?.created_at ? (
          <span className="ai-message__time">{formatDateTime(message.created_at, locale)}</span>
        ) : null}
      </div>

      <div className="ai-message__bubble">
        <div className="ai-message__content">{message?.content || ""}</div>
        {isAssistant ? <AssistantSections message={message} isAr={isAr} /> : null}
      </div>
    </article>
  );
}

export default function MessageList({
  messages = [],
  isAr = false,
  loading = false,
}) {
  const bottomRef = useRef(null);
  const sortedMessages = useMemo(() => {
    return [...messages].sort((left, right) => {
      return Number(left?.sequence || 0) - Number(right?.sequence || 0);
    });
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [sortedMessages, loading]);

  if (loading) {
    return (
      <div className="ai-chatbox__messages-state">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جارِ تحميل المحادثة..." : "Loading conversation..."}</p>
      </div>
    );
  }

  if (sortedMessages.length === 0) {
    return (
      <div className="ai-chatbox__messages-state ai-chatbox__messages-state--empty">
        <h3>{isAr ? "ابدأ محادثة جديدة" : "Start a new conversation"}</h3>
        <p>
          {isAr
            ? "يمكنك السؤال عن الطلبات، المنتجات، الكورسات، السياسات، أو أي شيء داخل المنصة."
            : "You can ask about orders, products, courses, policies, or anything inside the platform."}
        </p>
      </div>
    );
  }

  return (
    <div className="ai-chatbox__messages">
      {sortedMessages.map((message) => (
        <MessageBubble
          key={message.id || `${message.role}-${message.sequence}-${message.created_at || ""}`}
          message={message}
          isAr={isAr}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
